import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, LocateFixed, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, POPULAR } from "@/lib/api";
import { Button } from "@/components/ui/button";

function useCitySuggest(where, enabled) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!enabled || where.length < 2) {
      setItems([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/geo/suggest", { params: { q: where } });
        setItems(data);
      } catch (e) {
        setItems([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [where, enabled]);
  return items;
}

export function SearchBar({ initial = {}, dark }) {
  const navigate = useNavigate();
  const [q, setQ] = useState(initial.q || "");
  const [where, setWhere] = useState(initial.where || "");
  const [coords, setCoords] = useState(initial.lat ? { lat: initial.lat, lon: initial.lon } : null);
  const [focusWhere, setFocusWhere] = useState(false);
  const [locating, setLocating] = useState(false);
  const typed = useRef(false);
  const cities = useCitySuggest(where, focusWhere && typed.current);

  const submit = (e) => {
    e?.preventDefault();
    if (q.trim().length < 2) {
      toast.error("Indiquez ce que vous recherchez (métier, produit, marque…)");
      return;
    }
    const p = new URLSearchParams({ q: q.trim() });
    if (where) p.set("where", where);
    if (coords) {
      p.set("lat", coords.lat);
      p.set("lon", coords.lon);
    }
    navigate(`/recherche?${p.toString()}`);
  };

  const locate = () => {
    if (!navigator.geolocation) return toast.error("Géolocalisation non disponible");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const c = { lat: +pos.coords.latitude.toFixed(5), lon: +pos.coords.longitude.toFixed(5) };
        setCoords(c);
        typed.current = false;
        try {
          const { data } = await api.get("/geo/reverse", { params: c });
          setWhere(data.label || "Autour de moi");
        } catch (e) {
          setWhere("Autour de moi");
        }
        setLocating(false);
      },
      () => {
        setLocating(false);
        toast.error("Impossible d'obtenir votre position");
      },
      { timeout: 10000 }
    );
  };

  return (
    <form onSubmit={submit} className={`relative grid gap-2 rounded-2xl p-2 shadow-2xl md:grid-cols-[1.3fr_1fr_auto] ${dark ? "bg-white" : "border border-slate-200 bg-white shadow-slate-200/60"}`} data-testid="search-form">
      <label className="flex items-center gap-3 rounded-xl px-4 py-3 hover:bg-slate-50">
        <Search className="h-5 w-5 shrink-0 text-[#1E40AF]" />
        <span className="w-full">
          <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">Quoi ?</span>
          <input list="qfq-popular" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Dépanneur machine à laver, Bosch…" className="w-full bg-transparent text-base text-slate-900 outline-none placeholder:text-slate-400" data-testid="search-quoi-input" />
          <datalist id="qfq-popular">{POPULAR.map((p) => <option key={p} value={p} />)}</datalist>
        </span>
      </label>
      <div className="relative flex items-center gap-3 rounded-xl px-4 py-3 hover:bg-slate-50 md:border-l md:border-slate-200">
        <MapPin className="h-5 w-5 shrink-0 text-[#1E40AF]" />
        <span className="w-full">
          <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">Où ?</span>
          <input value={where} onFocus={() => setFocusWhere(true)} onBlur={() => setTimeout(() => setFocusWhere(false), 200)}
            onChange={(e) => { typed.current = true; setWhere(e.target.value); setCoords(null); }}
            placeholder="Ville ou code postal" className="w-full bg-transparent text-base text-slate-900 outline-none placeholder:text-slate-400" data-testid="search-ou-input" />
        </span>
        <button type="button" onClick={locate} title="Autour de moi" className="rounded-full p-2 text-slate-600 transition-colors hover:bg-[#EFF6FF] hover:text-[#1E40AF]" data-testid="geolocate-button">
          {locating ? <Loader2 className="h-5 w-5 animate-spin" /> : <LocateFixed className="h-5 w-5" />}
        </button>
        {cities.length > 0 && focusWhere && (
          <ul className="absolute left-0 right-0 top-full z-[1100] mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl" data-testid="city-suggestions">
            {cities.map((c, i) => (
              <li key={c.label + i}>
                <button type="button" className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50" data-testid={`city-suggestion-${i}`}
                  onMouseDown={() => { typed.current = false; setWhere(c.label); setCoords({ lat: c.lat, lon: c.lon }); }}>
                  {c.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Button type="submit" className="h-auto rounded-xl bg-[#1E40AF] px-8 py-4 text-base font-semibold hover:bg-[#1D4ED8] hover:text-white" data-testid="search-submit-button">
        Rechercher
      </Button>
    </form>
  );
}
